import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

/**
 * Custom dropdown that matches shop styling (rounded panel, inset chevron).
 * Fires the same synthetic event shape as &lt;select onChange&gt;: `{ target: { name, value } }`.
 */
const SelectField = ({
  id,
  name,
  value,
  onChange,
  options = [],
  disabled = false,
  triggerClassName = "rounded-xl py-2.5 pl-3 pr-3",
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const [menuRect, setMenuRect] = useState(null);

  const selected = options.find((o) => o.value === value) ?? options[0];
  const label = selected?.label ?? "";

  const updateRect = () => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuRect({
      top: r.bottom + 6,
      left: r.left,
      width: r.width,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return;
    }
    updateRect();
    const onScroll = () => updateRect();
    const onResize = () => updateRect();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (anchorRef.current?.contains(e.target)) return;
      const menu = document.getElementById(`${id}-listbox`);
      if (menu?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const fire = (nextValue) => {
    onChange({ target: { name, value: nextValue } });
    setOpen(false);
  };

  const list =
    open && menuRect ? (
      <ul
        id={`${id}-listbox`}
        role="listbox"
        className="max-h-56 overflow-auto rounded-xl border border-stone-200 bg-white py-1 shadow-xl ring-1 ring-black/5"
        style={{
          position: "fixed",
          top: menuRect.top,
          left: menuRect.left,
          width: menuRect.width,
          zIndex: 10050,
        }}
      >
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <li key={`${String(opt.value)}-${opt.label}`} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => fire(opt.value)}
                className={`flex w-full items-center px-3 py-2.5 text-left text-sm transition ${
                  active
                    ? "bg-rose-50 font-semibold text-rose-900"
                    : "text-stone-800 hover:bg-stone-50"
                }`}
              >
                {opt.label}
              </button>
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <>
      <div className="relative">
        <button
          ref={anchorRef}
          type="button"
          id={id}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? `${id}-listbox` : undefined}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={`flex w-full min-h-[2.75rem] items-center justify-between gap-2 border border-stone-200 bg-white text-left text-sm font-medium text-stone-900 shadow-sm transition hover:border-stone-300 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-300 disabled:cursor-not-allowed disabled:opacity-50 ${triggerClassName}`}
        >
          <span className="min-w-0 flex-1 truncate">{label}</span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-stone-500 transition-transform ${open ? "rotate-180" : ""}`}
            strokeWidth={2.25}
            aria-hidden
          />
        </button>
      </div>
      {list ? createPortal(list, document.body) : null}
    </>
  );
};

export default SelectField;
